import Header from "../../components/header";
import Footer from "../../components/footer";
import "./index.css";
import Input from "../../components/shared/Input";
import { useState } from "react";
import Button from "../../components/shared/Button";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { updateActiveStep } from "../../state/auth";
import { register, verify2fa } from "../../api/auth";
import { toast } from "react-toastify";
import { countries } from "../../constants/countries";

const Step1 = ({
  nickName,
  setNickName,
  email,
  setEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
}: any) => {
  return (
    <div className="flex flex-col gap-6 w-full mt-8">
      <div className="flex flex-col w-full gap-4 px-20 text-[18px] md:text-[20px] relative group">
        <p>Nickname</p>
        <Input
          className="!h-[51px]"
          type="text"
          value={nickName}
          onChange={(value) => setNickName(value)}
          placeholder="Nickname"
        />
        <div className="invisible hidden group-hover:visible absolute -right-[50%] 2xl:-right-[64%] -top-3 leading-[20px] text-[14px] 2xl:text-[18px] xl:flex h-fit z-[20] border border-[white] text-white text-sm rounded-sm shadow-lg transition-opacity duration-300">
          <div className="flex w-full h-full bg-[black] z-[20] p-2 pr-4">
            Your nickname must meet the following requirements:
            <br />
            -Do not contain swear, racist words or profanity
            <br />
            -Do not contain special characters ( _ ! : £ % ...)
            <br />
            -Contain at least 1 lowercase letter
            <br />
            -Be at least 3 characters long
            <br />
            -Can’t exceed 15 characters
            <br />
            -Can’t use spaces between words
          </div>
          <div className="absolute top-[calc(50%-20px)] skew-x-[45deg] -rotate-[22deg] -left-3 w-12 h-10 bg-[black] border border-[white] z-[10]"></div>
        </div>
      </div>
      <div className="flex flex-col gap-4 px-20 text-[18px] md:text-[20px]">
        <p>Email</p>
        <Input
          className="!h-[51px]"
          type="email"
          value={email}
          onChange={(value) => setEmail(value)}
          placeholder="Email"
        />
      </div>
      <div className="flex flex-col gap-4 px-20 text-[18px] md:text-[20px] relative group">
        <p>Password</p>
        <Input
          className="!h-[51px]"
          type="password"
          value={password}
          onChange={(value) => setPassword(value)}
          placeholder="Password"
        />
        <div className="invisible hidden group-hover:visible absolute -right-[50%] 2xl:-right-[64%] top-2 text-[14px] 2xl:text-[18px] xl:flex h-fit z-[20] border border-[white] text-white text-sm rounded-sm shadow-lg transition-opacity duration-300">
          <div className="flex w-full h-full bg-[black] z-[20] p-2 pr-4">
            Your password must meet the following requirements:
            <br />
            -Contain at least 1 uppercase letter
            <br />
            -Contain at least 1 lowercase letter
            <br />
            -Contain at least 1 special character
            <br />
            -Contain at least 1 number
            <br />
            -Be at least 8 characters long
          </div>
          <div className="absolute top-[calc(50%-20px)] skew-x-[45deg] -rotate-[22deg] -left-3 w-12 h-10 bg-[black] border border-[white] z-[10]"></div>
        </div>
      </div>
      <div className="flex flex-col gap-4 px-20 text-[18px] md:text-[20px]">
        <p>Confirm Password</p>
        <Input
          className="!h-[51px]"
          type="password"
          value={confirmPassword}
          onChange={(value) => setConfirmPassword(value)}
          placeholder="Password"
        />
      </div>
    </div>
  );
};

const Step2 = ({
  country,
  setCountry,
  setCountryCode,
  birthday,
  setBirthday,
  accountVisibility,
  setAccountVisibility,
  phoneNumber,
  setPhoneNumber,
  twoFactorAuth,
  setTwoFactorAuth,
  agreedToTerms,
  setAgreedToTerms,
  receiveNews,
  setReceiveNews,
}: any) => {
  const handleCountryChange = (countryName: string) => {
    setCountry(countryName);
    const selectedCountry = countries.find(c => c.value === countryName);
    if (selectedCountry) {
      setCountryCode(selectedCountry.dialing_code);
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full mt-8 px-20 text-[18px] md:text-[20px]">
      <div className="flex gap-4 w-full">
        <div className="flex flex-col w-1/2 gap-4">
          <p>Country</p>
          <Input
            type="select"
            className="!h-[51px]"
            value={country}
            onChange={handleCountryChange}
            placeholder="Canada"
            options={countries}
          />
        </div>
        <div className="flex flex-col w-1/2 gap-4">
          <p>Birthday Date</p>
          <Input
            className="!h-[51px]"
            type="date"
            value={birthday}
            onChange={(value) => setBirthday(value)}
            placeholder="DD/MM/YYYY"
          />
        </div>
      </div>
      <div className="flex flex-col w-full gap-4">
        <p>Account visibility</p>
        <Input
          type="select"
          className="!h-[51px]"
          value={accountVisibility}
          onChange={(value) => setAccountVisibility(value)}
          placeholder="Select"
          options={[
            { value: "public", label: "Public" },
            { value: "private", label: "Private" },
            { value: "friends", label: "Friends Only" },
          ]}
        />
      </div>
      <div className="flex flex-col w-full gap-4">
        <p>Phone Number</p>
        <Input
          className="!h-[51px]"
          type="tel"
          value={phoneNumber}
          onChange={(value) => setPhoneNumber(value)}
          placeholder="Phone Number"
        />
      </div>
      <div className="flex flex-col w-full gap-4">
        <p>Two Factor Authentication - optional</p>
        <Input
          type="select"
          className="!h-[51px]"
          value={twoFactorAuth}
          onChange={(value) => setTwoFactorAuth(value)}
          placeholder="Select method"
          options={[
            { value: "none", label: "None" },
            { value: "email", label: "Email" },
            { value: "google", label: "Google Authenticator" },
            { value: "microsoft", label: "Microsoft Authenticator" },
          ]}
        />
      </div>
      <div className="flex items-center gap-2 mt-4">
        <Input
          type="checkbox"
          checked={receiveNews}
          onChange={(value: boolean) => setReceiveNews(value)}
          className="h-5 w-5"
        />
        <p className="text-[14px]">
          I would like to receive iPlayMore news, special offers, releases and
          more. (This can be enabled anytime from your account management
          page).
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="checkbox"
          checked={agreedToTerms}
          onChange={(value: boolean) => setAgreedToTerms(value)}
          className="h-5 w-5"
        />
        <p className="text-[14px]">
          I agree to the Terms of Use & Privacy Policy or have parental consent.
        </p>
      </div>
    </div>
  );
};

const Step3 = ({ twoFactorAuth, setTwoFactorAuthCode, otpauthUrl }: any) => {
  switch (twoFactorAuth) {
    case "google":
    case "microsoft":
      return (
        <div className="flex flex-col items-center justify-center h-full mt-8 px-20">
          <p className="text-[18px] text-center">Scan the QR code with your authenticator app</p>
          {otpauthUrl && <img src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${otpauthUrl}`} alt="QR Code" className="my-4" />}
          <p className="text-[18px] text-center mt-2">Enter the code from your app</p>
          <Input
            type="text"
            onChange={(value) => setTwoFactorAuthCode(value)}
            placeholder="6-digit code"
            className="!h-[51px] mt-2"
          />
        </div>
      );
    case "email":
      return (
        <div className="flex flex-col items-center justify-center h-full mt-8 px-20">
          <p className="text-[18px] text-center">Check your email for a verification code</p>
          <Input
            type="text"
            onChange={(value) => setTwoFactorAuthCode(value)}
            placeholder="Verification code"
            className="!h-[51px] mt-2"
          />
        </div>
      );
    default:
      return (
        <div className="flex flex-col items-center justify-center h-full mt-8 px-20">
          <p className="text-[18px] text-center">
            You are about to create your iPlayMore account.
          </p>
          <p className="text-[18px] text-center mt-2">
            Click "Signup" to complete the registration.
          </p>
        </div>
      );
  }
};

const SuccessStep = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full mt-8">
      <p className="text-[24px] text-center">Account successfully created!</p>
      <p className="text-[16px] text-center mt-2">
        Redirecting to the site...
      </p>
    </div>
  );
};

const RegisterPage = () => {
  const [nickName, setNickName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [country, setCountry] = useState("Canada");
  const [countryCode, setCountryCode] = useState("+1");
  const [birthday, setBirthday] = useState("");
  const [accountVisibility, setAccountVisibility] = useState("public");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [twoFactorAuth, setTwoFactorAuth] = useState("none");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [receiveNews, setReceiveNews] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [twoFactorAuthCode, setTwoFactorAuthCode] = useState("");
  const [otpauthUrl, setOtpauthUrl] = useState("");

  const activeStep = useAppSelector((state) => state.authSlice.activeStep);
  const dispatch = useAppDispatch();

  const handleRegister = async () => {
    if (!agreedToTerms) {
      toast.error("You must agree to the Terms of Use & Privacy Policy.");
      return;
    }
    const res = await register({
      email,
      password,
      name: nickName,
      country,
      countryCode,
      birthday,
      accountVisibility,
      phoneNumber,
      twoFactorAuth,
      receiveNews,
    });
    if (res.success) {
      if (res.requires2fa) {
        setOtpauthUrl(res.otpauthUrl || "");
        dispatch(updateActiveStep(2)); // Go to Step 3 for 2FA
      } else {
        toast.success("Registration successful");
        localStorage.setItem("token", res.accessToken);
        setRegistrationSuccess(true);
        setTimeout(() => {
          window.location.href = "/";
        }, 3000);
      }
    } else {
      toast.error(res.message);
    }
  };

  const handleVerify2fa = async () => {
    const res = await verify2fa({ email, code: twoFactorAuthCode });
    if (res.success) {
      toast.success("Verification successful");
      localStorage.setItem("token", res.accessToken);
      setRegistrationSuccess(true);
      setTimeout(() => {
        window.location.href = "/";
      }, 3000);
    } else {
      toast.error(res.message);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (password !== confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
      if (!nickName || !email || !password) {
        toast.error("Please fill all fields in this step.");
        return;
      }
      dispatch(updateActiveStep(1));
    } else if (activeStep === 1) {
      if (!country || !birthday || !accountVisibility) {
        toast.error("Please fill all required fields in this step.");
        return;
      }
      if (!agreedToTerms) {
        toast.error("You must agree to the Terms of Use & Privacy Policy.");
        return;
      }
      handleRegister();
    }
  };

  const renderStepContent = () => {
    if (registrationSuccess) {
      return <SuccessStep />;
    }
    switch (activeStep) {
      case 0:
        return (
          <Step1
            nickName={nickName}
            setNickName={setNickName}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
          />
        );
      case 1:
        return (
          <Step2
            country={country}
            setCountry={setCountry}
            setCountryCode={setCountryCode}
            birthday={birthday}
            setBirthday={setBirthday}
            accountVisibility={accountVisibility}
            setAccountVisibility={setAccountVisibility}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            twoFactorAuth={twoFactorAuth}
            setTwoFactorAuth={setTwoFactorAuth}
            agreedToTerms={agreedToTerms}
            setAgreedToTerms={setAgreedToTerms}
            receiveNews={receiveNews}
            setReceiveNews={setReceiveNews}
          />
        );
      case 2:
        return <Step3 twoFactorAuth={twoFactorAuth} setTwoFactorAuthCode={setTwoFactorAuthCode} otpauthUrl={otpauthUrl} />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        backgroundImage: "url(assets/img/auth/bg.png)",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      className={`relative w-full h-full flex flex-col items-center text-[white]`}
    >
      <Header />
      <div className="w-full md:w-[676px] h-full flex flex-col items-center mt-5 z-10 mb-[166px]">
        <img
          alt="modal-logo"
          src="assets/img/login/logo.png"
          className="w-[252px] md:w-[276px]"
        />
        <p className="text-[16px] md:text-[20px]">
          Create your own iPlayMore account
        </p>
        <p className="text-[13px] md:text-[16px]">
          Your iPlayMore account will be linked to our games and experiences
        </p>
        <div className="w-full" style={{ minHeight: '450px' }}>
          {renderStepContent()}
        </div>
        {!registrationSuccess && (
          <div className="flex justify-between mt-20 px-[28px] text-[18px] md:text-[20px] w-full">
            <Button
              onClick={() => {
                dispatch(updateActiveStep(activeStep - 1));
              }}
              className="w-[98px] !h-[51px]"
              disabled={activeStep === 0 || activeStep === 2}
            >
              Back
            </Button>
            <div className="flex items-center gap-2">
              {[0, 1, 2].map((index) => (
                <button
                  key={index}
                  onClick={() => {
                    // Add validation before allowing step jumping
                    // dispatch(updateActiveStep(index));
                  }}
                  className={`w-5 h-5 border border-[#525252] rounded-full ${
                    activeStep === index ? "bg-[#1ab5f8]" : "bg-[#d9d9d9]"
                  }`}
                />
              ))}
            </div>
            <Button
              onClick={activeStep === 2 ? handleVerify2fa : handleNext}
              className="w-[98px] !h-[51px]"
            >
              {activeStep === 2 ? "Verify" : "Next"}
            </Button>
          </div>
        )}
      </div>
      <div className="md:w-[676px] w-full h-full absolute bg-[black] bg-opacity-[93%]"></div>
      <div className="w-full flex absolute bottom-0">
        <Footer type="sm" />
      </div>
    </div>
  );
};

export default RegisterPage;

